'use client';

import {
  useGetBrandsQuery,
  useGetCountiesQuery,
  useGetModelsQuery,
} from '@api/api';
import { useRouter, useSearchParams } from 'next/navigation';
import FilterAccordionItem from '@components/Filter/FilterAccordionItem';
import { Fragment, useEffect, useState } from 'react';
import useParamsEditor from '@/src/utils/useParamsEditor';
import { useForm } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import { allowedFilterParams } from '@api/allowedParams';
import { useLocale, useTranslations } from 'next-intl';
import getOption from '@/src/utils/getOption';
import {
  Category,
  getCategoriesData,
  getSubCategoryById,
} from '@/src/utils/getMainCategories';
import { useLocaleRoutes } from '@/src/utils/useLocaleRoutes';
import { TbFilter, TbFilterOff } from 'react-icons/tb';
import useLocaleSuffix from '@/src/utils/useLocaleSuffix';

export default function Filter({
  slug,
  category = false,
  redirect = false,
  subCat = false,
  categoriesData,
}: {
  slug: string;
  category?: Category | false | undefined | null;
  redirect?: boolean;
  subCat?: any;
  categoriesData?: any;
}) {
  const t = useTranslations('Filter');
  const c = useTranslations('categories');
  const g = useTranslations('general');
  const path = useLocaleRoutes();
  const { push } = useRouter();
  const { register, handleSubmit, getValues, setValue } = useForm();
  const [filterOn, setFilterOn] = useState(false);
  const [rerendered, rerender] = useState({});
  const [filterAnimate, setFilterAnimate] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');
  const [specificFilters, setSpecificFilters] = useState([] as string[]);
  const searchParams = useSearchParams();
  const editParam = useParamsEditor();
  const locale = useLocale();
  const mainCategory = getCategoriesData(slug);
  const localeSuffix = useLocaleSuffix();
  const q = (param: string) => {
    return searchParams.get(param) ? String(searchParams.get(param)) : '';
  };
  const [brandsQuery, setBrandQuery] = useState(
    q('category_id')
      ? q('category_id').split(',').join(',')
      : subCat
        ? String(subCat.id)
        : category && category.id
          ? String(category.id)
          : ''
  );

  const backdropClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as Element).classList.contains('backdropClick')) {
      closeFilter();
    }
  };

  useEffect(() => {
    if (subCat) {
      return;
    }
    let categoryIds = [];
    if (getValues('category_id')?.forEach) {
      categoryIds = getValues('category_id');
    }
    if (getValues('category_parent_id')?.forEach) {
      categoryIds = categoryIds.concat(getValues('category_parent_id'));
    }
    if (categoryIds.length) {
      setBrandQuery(categoryIds.join(','));
    } else if (category) {
      setBrandQuery(String(category.id));
    } else if (brandsQuery) {
      setBrandQuery('');
    }
  }, [getValues, rerendered]);

  useEffect(() => {
    allowedFilterParams.forEach((param) => {
      if (param === 'has_auction' && !searchParams.get('has_auction')) {
        setValue(param, ['0', '1']);
      } else if (param === 'category_id') {
        const val = searchParams.get(param)
          ? String(searchParams.get(param)).split(',')
          : '';
        setValue(param, val || '');
        setValue(param, val || '');
      } else if (
        ['category_parent_id', 'make_id', 'model_id', 'county_id'].includes(
          param
        )
      ) {
        setValue(
          param,
          searchParams.get(param)
            ? String(searchParams.get(param)).split(',')
            : ''
        );
      } else if (searchParams.get(param)) {
        setValue(param, searchParams.get(param));
      } else {
        setValue(param, '');
      }
    });
  }, [searchParams, setValue, subCat]);

  const { data: brandsData, isFetching: isFetchingBrands } =
    useGetBrandsQuery(brandsQuery);
  const { data: countiesData } = useGetCountiesQuery();
  const { data: modelsData } = useGetModelsQuery(q('make_id'), {
    skip: !q('make_id'),
  });

  const getSubcategoryData = (id: number) => {
    let subcat = null as any;
    categoriesData?.data?.forEach((cat: any) => {
      if (cat.id === id) {
        subcat = cat;
      }
    });
    return subcat;
  };
  const getCategoryCount = (id: number) => {
    return getSubcategoryData(id)?.ads;
  };

  const categories = getCategoriesData();
  useEffect(() => {
    /* case of being in a category page */
    if (!category || subCat) {
      return;
    }
    let specificFilters = [] as string[];
    if (category && mainCategory.sortfields) {
      specificFilters = mainCategory.sortfields.split(',');
    }
    let arrayOfFilters = [] as string[][];
    if (getValues('category_id')?.forEach) {
      getValues('category_id').forEach((element: any) => {
        const cat = getSubCategoryById(parseInt(element));
        if (cat?.sortfields) {
          arrayOfFilters.push(cat.sortfields.split(','));
        } else {
          arrayOfFilters.push([]);
        }
      });
    }
    const commonFilters = arrayOfFilters.length
      ? arrayOfFilters.reduce((commonElements, currentArray) => {
          return commonElements.filter((element) =>
            currentArray.includes(element)
          );
        })
      : [];
    specificFilters = specificFilters.concat(commonFilters);
    setSpecificFilters(specificFilters);
  }, [getValues, rerendered]);

  useEffect(() => {
    /* case of being in a subcategory page */
    if (!subCat) {
      return;
    }
    let specificFilters = [] as string[];
    if (category && mainCategory.sortfields) {
      specificFilters = mainCategory.sortfields.split(',');
    }
    let arrayOfFilters = [] as string[][];
    const cat = getSubCategoryById(parseInt(subCat.id));
    if (cat?.sortfields) {
      arrayOfFilters.push(cat.sortfields.split(','));
    } else {
      arrayOfFilters.push([]);
    }
    if (
      (subCat &&
        String(getOption('WEIGHT_CATEGORIES'))
          .split(',')
          .includes(String(subCat.id))) ||
      q('category_id')?.split(',').sort().join(',') ===
        String(getOption('WEIGHT_CATEGORIES')).split(',').sort().join(',') ||
      (getValues('category_id') &&
        getValues('category_id').length &&
        getValues('category_id').every &&
        getValues('category_id').every((element: any) => {
          return String(getOption('WEIGHT_CATEGORIES'))
            .split(',')
            .includes(element);
        }))
    ) {
      specificFilters.push('kg');
    }

    const commonFilters = arrayOfFilters.length
      ? arrayOfFilters.reduce((commonElements, currentArray) => {
          return commonElements.filter((element) =>
            currentArray.includes(element)
          );
        })
      : [];
    specificFilters = specificFilters.concat(commonFilters);
    setSpecificFilters(specificFilters);
  }, [getValues, rerendered]);

  useEffect(() => {
    /* case of being in all ads page */
    if (category) {
      return;
    }
    let specificFilters = [] as string[];
    let arrayOfFilters = [] as string[][];
    let handledCategories = [] as number[];
    let chosenCategories = getValues('category_id').length
      ? [...getValues('category_id')]
      : [];
    chosenCategories.forEach((element: any) => {
      const cat = getSubCategoryById(parseInt(element));
      let filters = [];
      if (cat?.sortfields) {
        filters = cat.sortfields.split(',');
      }
      const parentCat = getCategoriesData(cat.parentId);
      if (parentCat?.sortfields) {
        filters = filters.concat(parentCat.sortfields.split(','));
      }
      handledCategories.push(parseInt(parentCat.id));
      arrayOfFilters.push(filters);
    });
    if (getValues('category_parent_id')?.forEach) {
      getValues('category_parent_id').forEach((element: any) => {
        if (handledCategories.includes(parseInt(element))) {
          return;
        }
        const cat = getCategoriesData(parseInt(element));
        if (cat?.sortfields) {
          arrayOfFilters.push(cat.sortfields.split(','));
        } else {
          arrayOfFilters.push([]);
        }
      });
    }
    const commonFilters = arrayOfFilters.length
      ? arrayOfFilters.reduce((commonElements, currentArray) => {
          return commonElements.filter((element) =>
            currentArray.includes(element)
          );
        })
      : [];
    specificFilters = specificFilters.concat(commonFilters);
    setSpecificFilters(specificFilters);
  }, [getValues, rerendered]);

  const filter = (formData: any) => {
    setBrandFilter('');
    let query = {} as any;
    if (q('sort')) {
      query['sort'] = q('sort');
    }
    for (const param in formData) {
      if (!formData[param]) {
        continue;
      }
      if (
        param === 'has_auction' &&
        (formData[param].length === 0 || formData[param].length === 2)
      ) {
        continue;
      }
      if (
        ['category_id', 'category_parent_id', 'make_id', 'brand_id'].includes(
          param
        )
      ) {
        if (formData[param].length && typeof formData[param] === 'object') {
          query[param] = formData[param].join(',');
        } else if (formData[param].length) {
          query[param] = formData[param];
        }
        continue;
      }
      if (param === 'search' && formData[param]) {
        query['lang'] = localeSuffix('', false);
      }
      query[param] = formData[param];
    }
    closeFilter();
    const prefix =
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === locale ? '' : `/${locale}`;
    const queryString = Object.keys(query)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`
      )
      .join('&');
    if (redirect) {
      push(`${prefix}/${path('all')}?${queryString}`);
    } else {
      editParam(query, null, true);
    }
  };

  const [yearRangeMsg, setYearRangeMsg] = useState('');
  const [kmRangeMsg, setKmRangeMsg] = useState('');
  const [kgRangeMsg, setKgRangeMsg] = useState('');
  const [priceRangeMsg, setPriceRangeMsg] = useState('');
  const [hoursRangeMsg, setHoursRangeMsg] = useState('');
  const [hkRangeMsg, setHkRangeMsg] = useState('');
  const validateRange = (field: string) => {
    const val_le = parseInt(getValues(`${field}_le`));
    const val_ge = parseInt(getValues(`${field}_ge`));
    if (val_ge && val_le && val_le < val_ge) {
      switch (field) {
        case 'price':
          setPriceRangeMsg(t('price.validation.min'));
          return t('price.validation.min');
        case 'year':
          setYearRangeMsg(t('year.validation.min'));
          return t('year.validation.min');
        case 'meta_kg':
          setKgRangeMsg(t('weight.validation.min'));
          return t('weight.validation.min');
        case 'meta_km':
          setKmRangeMsg(t('mileage.validation.min'));
          return t('mileage.validation.min');
        case 'meta_hk':
          setHkRangeMsg(t('horsepower.validation.min'));
          return t('horsepower.validation.min');
        case 'meta_hours':
          setHoursRangeMsg(t('hours.validation.min'));
          return t('hours.validation.min');
      }
    }
    switch (field) {
      case 'price':
        setPriceRangeMsg('');
        break;
      case 'year':
        setYearRangeMsg('');
        break;
      case 'meta_kg':
        setKgRangeMsg('');
        break;
      case 'meta_km':
        setKmRangeMsg('');
        break;
      case 'meta_hk':
        setHkRangeMsg('');
        break;
      case 'meta_hours':
        setHoursRangeMsg('');
        break;
    }
    return undefined;
  };

  const reset = () => {
    Object.keys(getValues()).forEach((input) => {
      setValue(input, '');
    });
    setValue('has_auction', ['0', '1']);
  };

  const submitButton = (
    <input
      type='submit'
      className='cursor-pointer bg-black px-2 py-1 font-bold uppercase text-white transition-all hover:bg-white hover:text-dark'
      value={g('ok')}
    />
  );

  const closeFilter = () => {
    setFilterAnimate(false);
    document.body.classList.remove('overflow-hidden');
    setTimeout(() => setFilterOn(false), 500);
  };

  const openFilter = () => {
    setFilterOn(true);
    document.body.classList.add('overflow-hidden');
    setTimeout(() => setFilterAnimate(true), 10);
  };

  function nextLevelCategories(children: any, level = 1) {
    if (typeof children !== 'object' || !Object.keys(children).length) {
      return null;
    }

    let subcats = Object.values(children);

    // On main index page (level 1), sort by order property if available, otherwise alphabetically
    if (level === 1) {
      subcats = subcats.sort((a: any, b: any) => {
        // If both have order, sort by order
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // If only one has order, put it first
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        // Otherwise sort alphabetically
        return (
          a['name-' + locale]?.localeCompare(b['name-' + locale] || '') || 0
        );
      });
    }
    // For level 2 and deeper, always sort alphabetically
    else if (level >= 2) {
      subcats = subcats.sort((a: any, b: any) =>
        (a['name-' + locale] || '').localeCompare(b['name-' + locale] || '')
      );
    }
    return subcats.map((subcat: any) => {
      if (getCategoryCount(subcat.id) < 1) {
        return null;
      }
      return (
        <div className='ml-4' key={subcat.id}>
          <label className='flex gap-2'>
            <input
              type='checkbox'
              {...register('category_id', {
                onChange: () => {
                  rerender({});
                },
              })}
              value={subcat.id}
            />
            <span>{subcat['name-' + locale]}</span>
          </label>
          {nextLevelCategories(subcat.children, level + 1)}
        </div>
      );
    });
  }

  let subcatCounter = 0;

  return (
    <>
      <button
        className='flex h-full items-center gap-2 rounded-sm border border-brand bg-brand px-4 py-1 font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
        onClick={filterOn ? closeFilter : openFilter}
      >
        <TbFilter />
        {t('button.open')}
      </button>

      <div
        className={`backdropClick fixed inset-0 z-50 h-screen w-screen transition duration-500
          ${filterOn ? 'block' : 'hidden'}
          ${filterAnimate ? 'bg-black/50' : 'bg-black/0'}
          `}
        onClick={(e) => backdropClick(e)}
      >
        <section
          className={`fixed bottom-0 transition-all duration-500 ${filterAnimate ? 'right-0' : '-right-[460px]'} top-0 h-full w-[460px] max-w-full bg-brand p-8 text-dark sm:rounded-l-md`}
        >
          <div
            className={`fixed transition-all duration-500 ${filterAnimate ? 'right-0' : '-right-[460px]'} top-0 flex w-[460px] max-w-full flex-wrap justify-between gap-4 bg-brand px-8 py-4 md:flex-nowrap`}
          >
            <button
              type='button'
              className='order-1 flex grow items-center justify-center gap-1 bg-brand-dark px-4 py-2 font-bold uppercase text-black transition-all hover:bg-white hover:text-dark sm:order-1'
              onClick={reset}
            >
              <TbFilterOff />
              {t('button.reset')}
            </button>
            <button
              onClick={handleSubmit(filter)}
              className='order-3 flex w-full grow items-center justify-center gap-1 bg-black px-4 py-2 font-bold  uppercase text-white transition-all hover:bg-white hover:text-dark sm:order-2 sm:w-auto'
            >
              <TbFilter />
              {t('button.apply')}
            </button>
            <button
              className='order-2 block bg-black p-2 text-xl font-bold uppercase text-white transition-all hover:bg-white hover:text-dark sm:order-3'
              onClick={closeFilter}
            >
              <MdClose />
            </button>
          </div>
          <form
            className='absolute bottom-0 left-8 right-3 top-32 overflow-auto pb-8 pr-5 sm:top-20'
            onSubmit={handleSubmit(filter)}
          >
            <div className='mb-6 flex flex-col'>
              <span className='mb-2 font-bold uppercase'>
                {t('searchInCategory')}
                {slug === 'all'
                  ? c('all.slug')
                  : mainCategory['name-' + locale]}
              </span>
              <input
                className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                {...register('search')}
              />
            </div>
            {!!getOption('HAS_AUCTIONS') && !!getOption('HAS_FIXED_PRICE') && (
              <div className='mb-6 flex flex-col'>
                <span className='mb-2 font-bold uppercase'>
                  {t('typesOfSale.label')}
                </span>
                <label className='flex gap-2'>
                  <input
                    type='checkbox'
                    {...register('has_auction')}
                    value='1'
                  />
                  <span>{t('typesOfSale.auction')}</span>
                </label>
                <label className='flex gap-2'>
                  <input
                    type='checkbox'
                    {...register('has_auction')}
                    value='0'
                  />
                  <span>{t('typesOfSale.fixed')}</span>
                </label>
              </div>
            )}

            <section className='mb-4 block flex flex-col'>
              {Object.keys(categories).length > 1 && (
                <FilterAccordionItem
                  label={c('categories')}
                  active={!!q('category_id')}
                >
                  <div
                    className={`${!category ? 'max-h-[200px]' : ''} overflow-auto`}
                  >
                    {categories.map((cat: any) => {
                      if (category && cat.slug !== category.slug) {
                        return;
                      }
                      return (
                        <div key={cat.id} className={category ? '-ml-4' : ''}>
                          {!category && (
                            <label className='flex gap-2'>
                              <input
                                type='checkbox'
                                {...register('category_id', {
                                  onChange: () => {
                                    rerender({});
                                  },
                                })}
                                value={cat.id}
                              />
                              <span>{cat['name-' + locale]}</span>
                            </label>
                          )}
                          {subCat
                            ? nextLevelCategories(subCat.children)
                            : nextLevelCategories(cat.children)}
                        </div>
                      );
                    })}
                  </div>
                </FilterAccordionItem>
              )}
              {brandsData?.data && !!brandsData.data.length && (
                <FilterAccordionItem
                  label={t('brands.label')}
                  active={!!q('make_id')}
                >
                  <input
                    onChange={(e) => setBrandFilter(e.currentTarget.value)}
                    value={brandFilter}
                    className='mb-2 w-full p-2'
                    placeholder={t('brands.placeholder')}
                  />
                  <div className='max-h-[300px] overflow-auto'>
                    {[...brandsData.data]
                      .sort((brand1: any, brand2: any) => {
                        return brand1.name
                          .toLowerCase()
                          .localeCompare(brand2.name.toLowerCase());
                      })
                      .map((brand: any) => {
                        if (
                          brandFilter &&
                          !String(brand.name)
                            .toLocaleLowerCase()
                            .includes(brandFilter.toLowerCase())
                        ) {
                          return null;
                        }
                        return (
                          <Fragment key={brand.id}>
                            <label className='flex gap-2'>
                              <input
                                type='checkbox'
                                {...register('make_id')}
                                value={brand.id}
                              />
                              <span>{brand.name}</span>
                            </label>
                            {q('make_id')
                              .split(',')
                              .includes(String(brand.id)) &&
                              !!modelsData?.data?.length &&
                              modelsData?.data.map((model: any) => {
                                if (model.make_id !== brand.id) return;
                                return (
                                  <label
                                    className='flex gap-2 pl-3'
                                    key={model.id}
                                  >
                                    <input
                                      type='checkbox'
                                      {...register('model_id')}
                                      value={model.id}
                                    />
                                    <span>{model.name}</span>
                                  </label>
                                );
                              })}
                          </Fragment>
                        );
                      })}
                  </div>
                </FilterAccordionItem>
              )}
              <h3 className='my-2 border-b border-black pb-1 font-bold uppercase'>
                {t('year.label')}
              </h3>
              <div className='mb-1 flex gap-2'>
                <label className='flex grow'>
                  <span className='sr-only'>
                    {t('year.label')} {t('year.from')}
                  </span>
                  <input
                    type='number'
                    placeholder={t('year.from')}
                    className='w-full appearance-none rounded-sm px-2 py-1 text-dark'
                    {...register('year_ge', {
                      onChange: () => validateRange('year'),
                      validate: () => validateRange('year'),
                    })}
                  />
                </label>
                <label className='flex grow'>
                  <span className='sr-only'>
                    {t('year.label')} {t('year.to')}
                  </span>
                  <input
                    type='number'
                    placeholder={t('year.to')}
                    className='w-full appearance-none rounded-sm px-2 py-1 text-dark'
                    {...register('year_le', {
                      onChange: () => validateRange('year'),
                      validate: () => validateRange('year'),
                    })}
                  />
                </label>
                {submitButton}
              </div>
              {yearRangeMsg}
              {!getOption('HAS_AUCTIONS') && (
                <div className='my-3'>
                  <h3 className='my-2 border-b border-black pb-1 font-bold uppercase'>
                    {t('price.label')}
                  </h3>
                  <div className='mb-1 flex gap-2'>
                    <label>
                      <span className='sr-only'>
                        {t('price.label')} {t('price.from')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('price.from')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('price_ge', {
                          onChange: () => validateRange('price'),
                          validate: () => validateRange('price'),
                        })}
                      />
                    </label>
                    <label>
                      <span className='sr-only'>
                        {t('price.label')} {t('year.to')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('price.to')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('price_le', {
                          onChange: () => validateRange('price'),
                          validate: () => validateRange('price'),
                        })}
                      />
                    </label>
                    {submitButton}
                  </div>
                  {priceRangeMsg}
                </div>
              )}
              <FilterAccordionItem
                label={t('area.county')}
                active={q('county_id') ? true : false}
              >
                {countiesData?.data && !!countiesData.data.length && (
                  <>
                    <div>
                      {countiesData.data.map((county: any) => {
                        return (
                          <Fragment key={county.id}>
                            <label className='flex gap-2'>
                              <input
                                type='checkbox'
                                {...register('county_id')}
                                value={county.id}
                              />
                              <span>{county.name}</span>
                            </label>
                          </Fragment>
                        );
                      })}
                    </div>
                  </>
                )}
              </FilterAccordionItem>
              <label>
                <h3 className='my-2 border-b border-black pb-1 font-bold uppercase'>
                  {t('area.zip')}
                </h3>
                <div className='flex gap-2'>
                  <input
                    type='number'
                    {...register('zipcode')}
                    className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                  />
                  {submitButton}
                </div>
              </label>
              <label className='my-4'>
                <h3 className='my-2 border-b border-black pb-1 font-bold uppercase'>
                  {t('area.municipality')}
                </h3>
                <div className='flex gap-2'>
                  <input
                    {...register('municipality')}
                    className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                  />
                  {submitButton}
                </div>
              </label>
              {specificFilters.includes('km') && (
                <FilterAccordionItem
                  label={t('mileage.label')}
                  active={q('meta_km_ge') || q('meta_km_le') ? true : false}
                >
                  <div className='mb-1 flex gap-2'>
                    <label>
                      <span className='sr-only'>
                        {t('mileage.label')} {t('year.from')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('mileage.from')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_km_ge', {
                          onChange: () => validateRange('meta_km'),
                          validate: () => validateRange('meta_km'),
                        })}
                      />
                    </label>
                    <label>
                      <span className='sr-only'>
                        {t('mileage.label')} {t('year.to')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('mileage.to')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_km_le', {
                          onChange: () => validateRange('meta_km'),
                          validate: () => validateRange('meta_km'),
                        })}
                      />
                    </label>
                    {submitButton}
                  </div>
                  {kmRangeMsg}
                </FilterAccordionItem>
              )}
              {specificFilters.includes('hk') && (
                <FilterAccordionItem
                  label={t('horsepower.label')}
                  active={q('meta_hk_ge') || q('meta_hk_le') ? true : false}
                >
                  <div className='mb-1 flex gap-2'>
                    <label>
                      <span className='sr-only'>
                        {t('horsepower.label')} {t('horsepower.from')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('horsepower.from')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_hk_ge', {
                          onChange: () => validateRange('meta_hk'),
                          validate: () => validateRange('meta_hk'),
                        })}
                      />
                    </label>
                    <label>
                      <span className='sr-only'>
                        {t('horsepower.label')} {t('horsepower.to')}
                      </span>
                      <input
                        type='number'
                        placeholder={t('horsepower.to')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_hk_le', {
                          onChange: () => validateRange('meta_hk'),
                          validate: () => validateRange('meta_hk'),
                        })}
                      />
                    </label>
                    {submitButton}
                  </div>
                  {hkRangeMsg}
                </FilterAccordionItem>
              )}
              {specificFilters.includes('hours') && (
                <FilterAccordionItem
                  label={t('hours.label')}
                  active={
                    q('meta_hours_ge') || q('meta_hours_le') ? true : false
                  }
                >
                  <div className='mb-1 flex gap-2'>
                    <label>
                      <input
                        type='number'
                        placeholder={t('hours.from')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_hours_ge', {
                          onChange: () => validateRange('meta_hours'),
                          validate: () => validateRange('meta_hours'),
                        })}
                      />
                    </label>
                    <label>
                      <input
                        type='number'
                        placeholder={t('hours.to')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_hours_le', {
                          onChange: () => validateRange('meta_hours'),
                          validate: () => validateRange('meta_hours'),
                        })}
                      />
                    </label>
                    {submitButton}
                  </div>
                  {hoursRangeMsg}
                </FilterAccordionItem>
              )}
              {specificFilters.includes('kg') && (
                <FilterAccordionItem
                  label={
                    <>
                      {t('weight.label')}{' '}
                      <span className='font-normal'>(kg)</span>
                    </>
                  }
                  active={q('meta_kg_ge') || q('meta_kg_le') ? true : false}
                >
                  <div className='mb-1 flex gap-2'>
                    <label>
                      <input
                        type='number'
                        placeholder={t('weight.from')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_kg_ge', {
                          onChange: () => validateRange('meta_kg'),
                          validate: () => validateRange('meta_kg'),
                        })}
                      />
                    </label>
                    <label>
                      <input
                        type='number'
                        placeholder={t('weight.to')}
                        className='block w-full appearance-none rounded-sm px-2 py-1 text-dark'
                        {...register('meta_kg_le', {
                          onChange: () => validateRange('meta_kg'),
                          validate: () => validateRange('meta_kg'),
                        })}
                      />
                    </label>
                    {submitButton}
                  </div>
                  {kgRangeMsg}
                </FilterAccordionItem>
              )}
              {!!getOption('HAS_AUCTIONS') && (
                <FilterAccordionItem
                  label={t('auctions.label')}
                  active={q('auctions') ? true : false}
                >
                  <div className='flex flex-col gap-1'>
                    <label>
                      <input type='radio' {...register('auctions')} value='' />{' '}
                      {t('auctions.onlyActive')}
                    </label>
                    <label>
                      <input
                        type='radio'
                        {...register('auctions')}
                        value='3,4,8'
                      />{' '}
                      {t('auctions.includeConcluded')}
                    </label>
                    <label>
                      <input
                        type='radio'
                        {...register('auctions')}
                        value='4,8'
                      />{' '}
                      {t('auctions.onlyConcluded')}
                    </label>
                  </div>
                </FilterAccordionItem>
              )}
            </section>
            <input
              type='submit'
              className='block w-full cursor-pointer bg-black px-2 py-2 font-bold uppercase text-white transition-all hover:bg-white hover:text-dark'
              value={t('button.applyFilters')}
            />
          </form>
        </section>
      </div>
    </>
  );
}
